import React from "react";
import "./ResultSurvey.scss";
import { translate } from "react-switch-lang";

const ResultSurvey = ({ results, isAdmin, t }) => {
  const surveyData = results?.surveyData || [];
  // console.log("Survey Data:->>>>>>>>>>>>>>>>", surveyData);

  const renderStars = (avgRating) => {
    const rounded = Math.round(avgRating * 2) / 2;
    const fullStars = Math.floor(rounded);
    const isHalf = rounded - fullStars === 0.5;

    return Array.from({ length: 5 }, (_, idx) => {
      if (idx < fullStars) {
        return (
          <span key={idx} className="filled">
            ★
          </span>
        );
      } else if (idx === fullStars && isHalf) {
        return (
          <span key={idx} className="half">
            ★
          </span>
        );
      } else {
        return <span key={idx}>★</span>;
      }
    });
  };

  // Helper function to get selected option (for single selection)
  const getSelectedOption = (result) => {
    const selectedEntry = Object.entries(result).find(
      ([option, details]) => details.isSelect
    );
    return selectedEntry ? selectedEntry[0] : null;
  };

  // Helper function to get all selected options (for checkbox - multiple selection)
  const getSelectedOptions = (result) => {
    const selectedOptions = Object.entries(result)
      .filter(([option, details]) => details.isSelect)
      .map(([option]) => option);

    return selectedOptions.length > 0 ? selectedOptions : ["No selection"];
  };

  // ---------- ADMIN VIEW ----------
  if (isAdmin) {
    return (
      <div className="result-container-1">
        <div className="admin-heading">
          {surveyData && surveyData.length > 0 ? (
            surveyData.map((q, i) => (
              <div className="result-card" key={i}>
                <h2>Question: {q.question}</h2>
                {q.arabicTitle && (
                  <h3 className="question-text-arabic">{q.arabicTitle}</h3>
                )}

                {["text", "comment"].includes(q.type) ? (
                  <p className="no-aggregated-data">📝 No aggregated data</p>
                ) : q.type === "rating" ? (
                  <div className="rating-stars">
                    {renderStars(q.result?.averageRating || 0)}
                  </div>
                ) : q.type === "boolean" ? (
                  <div className="bar-group">
                    {Object.entries(q.result).map(([option, details], j) => {
                      const percentage = details.percentage || 0;
                      const barClass = "bar";

                      return (
                        <div className="bar-item" key={j}>
                          <div className="label">{option}</div>
                          <div className="bar-wrapper">
                            <div
                              className={barClass}
                              style={{ width: `${percentage}%` }}
                            ></div>
                            <span className="percentage">{percentage}%</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="bar-group">
                    {Object.entries(q.result).map(([option, info], j) => (
                      <div className="bar-item" key={j}>
                        <div className="label">{option}</div>
                        <div className="bar-wrapper">
                          <div
                            className="bar"
                            style={{ width: `${info.percentage || 0}%` }}
                          ></div>
                          <span className="percentage">
                            {info.percentage ?? 0}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="no-data-message">
              <p>📭 No responses received yet</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ---------- USER INDIVIDUAL VIEW ----------
  return (
    <div className="result-container-1">
      <div className="admin-heading">
        {surveyData.map((q, i) => (
          <div className="result-card" key={i}>
            <h2>Question: {q.question}</h2>
            {q.arabicTitle && (
              <h3 className="question-text-arabic">{q.arabicTitle}</h3>
            )}

            {["text", "comment"].includes(q.type) ? (
              <div>
                <div className="user-selection">
                  Your answer: <strong>{q.result?.value || "No answer"}</strong>
                </div>
              </div>
            ) : q.type === "rating" ? (
              <div>
                <div className="user-selection">
                  You selected: {renderStars(q.result?.userRating || 0)}
                </div>
                <div className="rating-stars">
                  Average: {renderStars(q.result?.averageRating || 0)}
                </div>
              </div>
            ) : q.type === "checkbox" ? (
              <div>
                {/* Show user's multiple selections for checkbox */}
                <div className="user-selection">
                  You selected:{" "}
                  <strong>{getSelectedOptions(q.result).join(", ")}</strong>
                </div>

                {/* Show bars for all options */}
                <div className="bar-group">
                  {Object.entries(q.result).map(([option, details], j) => {
                    const percentage = details.percentage || 0;
                    const barClass = "bar blue";

                    return (
                      <div className="bar-item" key={j}>
                        <div className="label">
                          {option}
                          {details.isSelect}
                        </div>
                        <div className="bar-wrapper">
                          <div
                            className={barClass}
                            style={{ width: `${percentage}%` }}
                          ></div>
                          <span className="percentage">{percentage}%</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : q.type === "boolean" ? (
              <div>
                {/* Show user's selection for boolean (Yes/No) */}
                <div className="user-selection">
                  You selected:{" "}
                  <strong>
                    {getSelectedOption(q.result) || "No selection"}
                  </strong>
                </div>

                {/* Show bars for Yes/No options */}
                <div className="bar-group">
                  {Object.entries(q.result).map(([option, details], j) => {
                    const percentage = details.percentage || 0;
                    const barClass = "bar blue";

                    return (
                      <div className="bar-item" key={j}>
                        <div className="label">
                          {option}
                          {details.isSelect}
                        </div>
                        <div className="bar-wrapper">
                          <div
                            className={barClass}
                            style={{ width: `${percentage}%` }}
                          ></div>
                          <span className="percentage">{percentage}%</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div>
                {/* Show user's single selection for other types (radiogroup, dropdown) */}
                <div className="user-selection">
                  You selected:{" "}
                  <strong>
                    {getSelectedOption(q.result) || "No selection"}
                  </strong>
                </div>

                {/* Show bars for all options */}
                <div className="bar-group">
                  {Object.entries(q.result).map(([option, details], j) => {
                    const percentage = details.percentage || 0;
                    const barClass = "bar blue";

                    return (
                      <div className="bar-item" key={j}>
                        <div className="label">
                          {option}
                          {details.isSelect}
                        </div>
                        <div className="bar-wrapper">
                          <div
                            className={barClass}
                            style={{ width: `${percentage}%` }}
                          ></div>
                          <span className="percentage">{percentage}%</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default translate(ResultSurvey);
